import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, Lock, IndianRupee } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { fetchProduct, fetchLawyers, fetchPaymentConfig, createOrder, verifyOrder, reconcileOrder } from '../lib/api';

const formatINR = (rupees) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(rupees);

const parseFee = (feeStr = '') => {
  const digits = feeStr.replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type');
  const id = searchParams.get('id');

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentEnabled, setPaymentEnabled] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!type || !id) {
      toast.error('Missing checkout details.');
      navigate('/shop');
      return;
    }
    setLoading(true);
    Promise.all([
      type === 'product'
        ? fetchProduct(id)
        : fetchLawyers().then((all) => all.find((l) => l.id === id)),
      fetchPaymentConfig().catch(() => ({ enabled: false })),
    ])
      .then(([itemData, cfg]) => {
        if (!itemData) {
          toast.error('Item not found.');
          navigate(type === 'product' ? '/shop' : '/find-a-lawyer');
          return;
        }
        setItem(itemData);
        setPaymentEnabled(Boolean(cfg?.enabled));
      })
      .catch(() => toast.error('Could not load item.'))
      .finally(() => setLoading(false));
  }, [type, id, navigate]);

  const amountRupees = item
    ? (type === 'product' ? item.price_inr : parseFee(item.fee))
    : 0;

  const displayName = item
    ? (type === 'product' ? item.name : `Consultation with ${item.name}`)
    : '';

  const openRazorpay = (orderRes) => {
    if (!window.Razorpay) {
      toast.error('Payment library did not load. Please refresh the page.');
      return;
    }
    const options = {
      key: orderRes.razorpay_key_id,
      amount: orderRes.amount_paise,
      currency: orderRes.currency,
      order_id: orderRes.razorpay_order_id,
      name: 'LawForLayman',
      description: orderRes.item_name,
      prefill: {
        name: orderRes.customer.name,
        email: orderRes.customer.email,
        contact: orderRes.customer.phone,
      },
      theme: { color: '#E89530' },
      handler: async (res) => {
        try {
          await verifyOrder({
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          navigate(`/shop/success/${orderRes.order_id}`);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'Payment verification failed. Please contact support.');
        }
      },
      modal: {
        ondismiss: async () => {
          try {
            const res = await reconcileOrder(orderRes.order_id);
            if (res.status === 'paid') {
              navigate(`/shop/success/${orderRes.order_id}`);
              return;
            }
            if (res.status === 'failed') {
              toast.error('Payment failed', {
                description: 'Razorpay reported the payment did not go through. If any amount was debited it will be auto-refunded within 5\u20137 days. Please try again with a different UPI ID or card.',
              });
              return;
            }
          } catch (_e) {
            // fall through
          }
          toast('Payment cancelled', {
            description: 'If your money was debited, please refresh in a minute or contact support with your order reference.',
          });
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      const desc = resp?.error?.description || 'Payment failed. Please try again.';
      toast.error(desc);
    });
    rzp.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill your name, email and phone.');
      return;
    }
    if (!paymentEnabled) {
      toast.error('Online payments are not yet configured. Please contact support.');
      return;
    }
    setSubmitting(true);
    try {
      const orderRes = await createOrder({
        order_type: type,
        item_id: id,
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        notes: form.notes.trim(),
      });
      openRazorpay(orderRes);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not initiate payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !item) {
    return (
      <div className="flex items-center justify-center py-32 text-[#5a5a5a]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">CHECKOUT</p>
          <h1 className="font-serif-besley text-4xl md:text-5xl mb-3">Complete your payment</h1>
          <p className="text-[#C9BFA8] max-w-2xl">Secure INR payments via Razorpay. UPI, cards, wallets and net banking are all supported.</p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-14">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white border border-[#E8DCC0] rounded-2xl p-8 space-y-5">
              <h2 className="font-serif-besley text-2xl text-[#0F1B2D]">Your details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-[#0F1B2D]">Full name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 border-[#E8DCC0]" />
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Phone *</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-11 border-[#E8DCC0]" />
                </div>
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11 border-[#E8DCC0]" />
                <p className="text-xs text-[#5a5a5a] mt-1.5">Receipt and download links will be sent here.</p>
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Notes (optional)</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 border-[#E8DCC0]" placeholder="Anything you'd like us to know about your order?" />
              </div>

              {!paymentEnabled && (
                <div className="p-4 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  Online payments are not yet configured. Please contact <a className="underline" href="mailto:contact@lawforlayman.com">contact@lawforlayman.com</a> to complete this purchase.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !paymentEnabled}
                className="btn-orange w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {submitting ? 'Preparing…' : `Pay ${formatINR(amountRupees)} securely`}
              </button>
              <p className="text-xs text-[#5a5a5a] flex items-center gap-2 justify-center">
                <ShieldCheck className="w-3.5 h-3.5" /> Payments processed by Razorpay. We never store your card details.
              </p>
            </form>
          </div>

          <aside className="lg:col-span-2">
            <div className="bg-[#EDE3CC] border border-[#D6CAB0] rounded-2xl p-8 sticky top-6">
              <p className="text-xs tracking-[0.25em] text-[#A36B1F] mb-3">ORDER SUMMARY</p>
              <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-2">{displayName}</h3>
              {type === 'product' && item.description ? (
                <p className="text-sm text-[#3a3a3a] leading-relaxed mb-4">{item.description}</p>
              ) : null}
              {type === 'consultation' ? (
                <p className="text-sm text-[#3a3a3a] leading-relaxed mb-4">
                  {item.specialty} · {item.city} · {item.experience} experience
                </p>
              ) : null}
              <div className="border-t border-[#D6CAB0] pt-4 mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5a5a5a]">Subtotal</span>
                  <span className="text-[#0F1B2D] font-medium">{formatINR(amountRupees)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5a5a5a]">Taxes</span>
                  <span className="text-[#0F1B2D]">Included</span>
                </div>
                <div className="border-t border-[#D6CAB0] pt-3 mt-3 flex justify-between items-center">
                  <span className="text-[#0F1B2D] font-medium">Total due</span>
                  <span className="font-serif-besley text-3xl text-[#0F1B2D] inline-flex items-center">
                    <IndianRupee className="w-5 h-5 mr-0.5" />{amountRupees.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
